/**
 * Auto-update: a single-page app keeps running its already-loaded JS even
 * after a new deploy, so operators can sit on a stale panel for hours. This
 * polls the (no-cache) index.html, and when it starts referencing a different
 * hashed bundle than the one currently running, reloads once to pick it up.
 */
function currentBundle(): string | null {
  // Vite emits <script type="module" src="/assets/index-XXXX.js">.
  const script = [...document.querySelectorAll("script[src]")].find((s) =>
    (s as HTMLScriptElement).src.includes("/assets/index-"),
  ) as HTMLScriptElement | undefined;
  const match = script?.src.match(/assets\/index-[^"]+\.js/);
  return match ? match[0] : null;
}

async function deployedBundle(): Promise<string | null> {
  try {
    const html = await (await fetch("/index.html", { cache: "no-store" })).text();
    return html.match(/assets\/index-[^"]+\.js/)?.[0] ?? null;
  } catch {
    return null;
  }
}

/**
 * True when reloading right now would throw away something the operator typed.
 *
 * The reload fires on window focus, so without this a cashier who tabs away
 * mid-receipt to check a number comes back to an empty form — the update
 * silently eats the entry. An open dialog counts as work in progress: in this
 * panel every dialog is a form.
 */
function hasUnsavedWork(): boolean {
  if (document.querySelector('[role="dialog"]')) return true;
  const fields = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    "input, textarea",
  );
  for (const field of fields) {
    if (field.type === "hidden" || field.disabled || field.readOnly) continue;
    if (field.value && field.value !== field.defaultValue) return true;
  }
  return false;
}

export function startAutoUpdate(intervalMs = 60_000): void {
  const running = currentBundle();
  if (!running) return;
  let reloading = false;

  const check = async () => {
    if (reloading) return;
    const latest = await deployedBundle();
    if (!latest || latest === running) return;
    // Deferred, not skipped: the next focus or tick tries again, so the update
    // lands as soon as the form is closed or cleared.
    if (hasUnsavedWork()) return;
    reloading = true;
    window.location.reload();
  };

  // Check on focus/visibility (operators tab away for long stretches) and on a timer.
  window.addEventListener("focus", check);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void check();
  });
  window.setInterval(check, intervalMs);
}
